<script setup>
import { onMounted, onBeforeUnmount, ref, computed } from 'vue'

const props = defineProps({
  deliveryId:  { type: [Number, String], required: true },
  pollEveryMs: { type: Number, default: 5000 },
  // Drop-target/return coords from the rental — used to anchor the
  // initial view when there are zero positions yet.
  destination: { type: Object, default: null }, // { lat, lng, label }
})

const mapEl     = ref(null)
const positions = ref([])
const status    = ref(null)
const lastSeen  = ref(null)
const error     = ref(null)

let map        = null
let driverMarker  = null
let pathLine     = null
let destMarker = null
let pollTimer  = null
let resizeObs  = null
let stoppedFitting = false

const lastFixAgeSec = computed(() => {
  if (!lastSeen.value) return null
  return Math.round((Date.now() - new Date(lastSeen.value).getTime()) / 1000)
})

function formatAge(sec) {
  if (sec == null) return ''
  if (sec < 60) return `${sec}s ago`
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`
  return `${Math.round(sec / 3600)}h ago`
}

async function fetchPositions() {
  try {
    const since = positions.value.length ? positions.value[positions.value.length - 1].recorded_at : null
    const url = `/deliveries/${props.deliveryId}/positions${since ? `?since=${encodeURIComponent(since)}` : ''}`
    const res = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
    if (!res.ok) { error.value = `HTTP ${res.status}`; return }
    const data = await res.json()
    status.value = data.status
    if (data.last_seen) lastSeen.value = data.last_seen
    if (data.positions?.length) {
      positions.value.push(...data.positions)
      drawDriver()
    }
    error.value = null
  } catch (e) {
    error.value = e?.message || 'fetch_failed'
  }
}

function drawDriver() {
  if (!map || !positions.value.length) return
  const L = window.L
  const last = positions.value[positions.value.length - 1]

  const accent = getComputedStyle(document.documentElement).getPropertyValue('--accent').trim() || '#2563eb'
  if (driverMarker) {
    driverMarker.setLatLng([last.lat, last.lng])
  } else {
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:18px;height:18px;border-radius:50%;background:${accent};border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.4)"></div>`,
      iconSize:   [18, 18],
      iconAnchor: [9, 9],
    })
    driverMarker = L.marker([last.lat, last.lng], { icon }).addTo(map)
  }

  const latlngs = positions.value.map(p => [p.lat, p.lng])
  if (pathLine) {
    pathLine.setLatLngs(latlngs)
  } else {
    pathLine = L.polyline(latlngs, { color: accent, weight: 4, opacity: 0.6 }).addTo(map)
  }

  // Auto-fit on the first time we get fixes; afterwards leave the user in
  // control so panning isn't yanked.
  if (!stoppedFitting && latlngs.length) {
    const bounds = pathLine.getBounds()
    if (destMarker) bounds.extend(destMarker.getLatLng())
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 })
    if (latlngs.length >= 2) stoppedFitting = true
  }
}

onMounted(async () => {
  const L = (await import('leaflet')).default
  await import('leaflet/dist/leaflet.css')
  window.L = L

  // Default center: destination if known, else somewhere reasonable in PHK.
  const center = props.destination?.lat && props.destination?.lng
    ? [props.destination.lat, props.destination.lng]
    : [7.8804, 98.3923] // Phuket Town
  map = L.map(mapEl.value).setView(center, 13)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map)

  if (props.destination?.lat && props.destination?.lng) {
    const danger = getComputedStyle(document.documentElement).getPropertyValue('--danger').trim() || '#dc2626'
    const icon = L.divIcon({
      className: '',
      html: `<div style="width:14px;height:14px;border-radius:50%;background:${danger};border:2px solid white"></div>`,
      iconSize:   [14, 14],
      iconAnchor: [7, 7],
    })
    destMarker = L.marker([props.destination.lat, props.destination.lng], { icon })
      .addTo(map).bindTooltip(props.destination.label || 'Destination')
  }

  await fetchPositions()
  pollTimer = setInterval(fetchPositions, props.pollEveryMs)
  resizeObs = new ResizeObserver(() => { if (map) map.invalidateSize() })
  resizeObs.observe(mapEl.value)
})

onBeforeUnmount(() => {
  if (pollTimer) clearInterval(pollTimer)
  if (resizeObs) resizeObs.disconnect()
  if (map)       { map.remove(); map = null }
})
</script>

<template>
  <div class="livedelivery">
    <div class="livedelivery__hdr">
      <span :class="['livedelivery__pill', `livedelivery__pill--${status || 'unknown'}`]">{{ status || '…' }}</span>
      <span
        v-if="lastSeen"
        class="livedelivery__age"
      >Last fix {{ formatAge(lastFixAgeSec) }}</span>
      <span
        v-else
        class="livedelivery__age"
      >No fixes yet</span>
      <span
        v-if="error"
        class="livedelivery__err"
      >{{ error }}</span>
    </div>
    <div
      ref="mapEl"
      class="livedelivery__map"
    />
  </div>
</template>

<style scoped>
.livedelivery { display: flex; flex-direction: column; gap: 0.5rem; height: 100%; }
.livedelivery__hdr {
  display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap;
  color: var(--inkSoft); font-size: 0.875rem;
}
.livedelivery__pill {
  display: inline-block; padding: 0.15rem 0.55rem; border-radius: 999px;
  font-size: 0.75rem; font-weight: 600;
}
.livedelivery__pill--assigned    { background: color-mix(in oklch, var(--accent) 14%, var(--surface)); color: var(--accent); }
.livedelivery__pill--in_progress { background: color-mix(in oklch, var(--accent) 22%, var(--surface)); color: var(--accent); }
.livedelivery__pill--completed   { background: color-mix(in oklch, var(--ok) 14%, var(--surface));     color: var(--ok); }
.livedelivery__pill--cancelled   { background: color-mix(in oklch, var(--muted) 14%, var(--surface));  color: var(--muted); }
.livedelivery__pill--unknown     { background: var(--surfaceAlt); color: var(--muted); }
.livedelivery__age { color: var(--muted); }
.livedelivery__err { color: var(--danger); }
.livedelivery__map {
  flex: 1;
  width: 100%;
  min-height: 360px;
  border: 1px solid var(--line);
  border-radius: var(--radius-table, 8px);
  overflow: hidden;
}
</style>
